import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Avatar,
  Divider,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from "@mui/icons-material";

const GalleryImageUpload = ({ onUpload, onCancel, loading }) => {
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) =>
    setFiles((prev) => [...prev, ...Array.from(e.target.files)]);
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    setFiles((prev) => [...prev, ...Array.from(e.dataTransfer.files)]);
  };
  const handleRemoveFile = (index) =>
    setFiles((prev) => prev.filter((_, i) => i !== index));

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (files.length === 0) return;

    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress((prev) =>
        prev >= 90 ? (clearInterval(interval), 90) : prev + 10,
      );
    }, 300);

    try {
      await onUpload(files);
      clearInterval(interval);
      setUploadProgress(100);
      setFiles([]); // reset selected files after successful upload
    } catch (err) {
      clearInterval(interval);
      setUploadProgress(0);
    }
  };
  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Paper
        variant="outlined"
        sx={{
          p: 4,
          textAlign: "center",
          borderColor: dragOver ? "primary.main" : "grey.300",
          borderWidth: dragOver ? 2 : 1,
          bgcolor: dragOver ? "primary.50" : "transparent",
          transition: "all 0.3s",
          cursor: "pointer",
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <CloudUploadIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
          Drag and drop gallery images here, or click to browse
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Supports: JPG, PNG, WEBP (Max 10MB each)
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />
      </Paper>

      {files.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Files ({files.length})
          </Typography>
          <Paper variant="outlined" sx={{ maxHeight: 200, overflow: "auto" }}>
            <List dense>
              {files.map((file, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleRemoveFile(index)}
                        color="error"
                        disabled={loading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar
                        variant="rounded"
                        src={URL.createObjectURL(file)}
                        sx={{ width: 48, height: 48 }}
                      >
                        <ImageIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={file.name}
                      secondary={formatFileSize(file.size)}
                      primaryTypographyProps={{
                        variant: "body2",
                        noWrap: true,
                      }}
                      secondaryTypographyProps={{ component: "div" }}
                    />
                  </ListItem>
                  {index < files.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>
      )}

      {loading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            Uploading... {uploadProgress}%
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={files.length === 0 || loading}
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          {loading ? "Uploading..." : `Upload ${files.length} Images`}
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default GalleryImageUpload;
